import {
  isRankingMetricField,
  parseRankingKpiMetric,
  type RankingKpiMetric,
} from "../../utils/kpiRankingMetric";

export type { RankingKpiMetric };

export type SportsbookRankingRow = {
  name: string;
  abbr?: string;
  turnover: RankingKpiMetric;
  pnl: RankingKpiMetric;
  margin_pct: RankingKpiMetric;
  total_bets: RankingKpiMetric;
  avg_bet_amount: RankingKpiMetric;
  total_users: RankingKpiMetric;
};

type RawRankingEntry = {
  name?: string;
  sport_name?: string;
  league_name?: string;
  abbr?: string;
  turnover?: unknown;
  pnl?: unknown;
  profit_loss?: unknown;
  profit_and_loss?: unknown;
  margin_pct?: unknown;
  total_bets?: unknown;
  avg_bet_amount?: unknown;
  total_users?: unknown;
};

const NESTED_ARRAY_KEYS = [
  "items",
  "sports",
  "top_sports",
  "leagues",
  "top_leagues",
  "rows",
  "states",
  "by_state",
] as const;

const RANKINGS_META_KEYS = new Set(["all", "meta", "filters", "summary"]);

export const TOP_SPORTS_METRIC_KEYS = [
  "turnover",
  "pnl",
  "margin_pct",
  "total_bets",
  "avg_bet_amount",
  "total_users",
] as const;

export type SportsbookTopSportsMetricKey =
  (typeof TOP_SPORTS_METRIC_KEYS)[number];

export type SportsbookTopSportsData = {
  byMetric: Partial<Record<SportsbookTopSportsMetricKey, SportsbookRankingRow[]>>;
};

/** Alias — Top Leagues uses the same envelope as Top Sports. */
export type SportsbookTopLeaguesData = SportsbookTopSportsData;

const mapEntryToRow = (
  name: string,
  entry: RawRankingEntry,
): SportsbookRankingRow => ({
  name: entry.name ?? entry.sport_name ?? entry.league_name ?? name,
  abbr: entry.abbr,
  turnover: parseRankingKpiMetric(entry.turnover),
  pnl: parseRankingKpiMetric(
    entry.pnl ?? entry.profit_loss ?? entry.profit_and_loss,
  ),
  margin_pct: parseRankingKpiMetric(entry.margin_pct),
  total_bets: parseRankingKpiMetric(entry.total_bets),
  avg_bet_amount: parseRankingKpiMetric(entry.avg_bet_amount),
  total_users: parseRankingKpiMetric(entry.total_users),
});

const isMetricEntry = (value: unknown): value is RawRankingEntry => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const entry = value as RawRankingEntry;
  return (
    isRankingMetricField(entry.turnover) ||
    isRankingMetricField(entry.pnl) ||
    isRankingMetricField(entry.profit_loss) ||
    isRankingMetricField(entry.profit_and_loss) ||
    isRankingMetricField(entry.margin_pct) ||
    isRankingMetricField(entry.total_bets) ||
    isRankingMetricField(entry.avg_bet_amount) ||
    isRankingMetricField(entry.total_users)
  );
};

/**
 * v2 analytics endpoints may return either an array of rows or a keyed object
 * (sport/state name -> metrics). Coerce every supported shape into an array.
 * Metric fields accept bare numbers or `{ value, percent_change }` envelopes.
 */
export const normalizeSportsbookRankingRows = (
  data: unknown,
): SportsbookRankingRow[] => {
  if (!data) return [];

  if (Array.isArray(data)) {
    return data.map((entry, index) => {
      if (isMetricEntry(entry)) {
        return mapEntryToRow(
          entry.name ??
            entry.sport_name ??
            entry.league_name ??
            `Row ${index + 1}`,
          entry,
        );
      }
      return mapEntryToRow(`Row ${index + 1}`, {});
    });
  }

  if (typeof data !== "object") return [];

  const record = data as Record<string, unknown>;
  for (const key of NESTED_ARRAY_KEYS) {
    const nested = record[key];
    if (Array.isArray(nested)) {
      return normalizeSportsbookRankingRows(nested);
    }
  }

  return Object.entries(record)
    .filter(([, value]) => isMetricEntry(value))
    .map(([key, value]) => mapEntryToRow(key, value as RawRankingEntry));
};

const buildByMetricFromRows = (
  rows: SportsbookRankingRow[],
): SportsbookTopSportsData["byMetric"] => {
  if (rows.length === 0) return {};
  return TOP_SPORTS_METRIC_KEYS.reduce<
    Partial<Record<SportsbookTopSportsMetricKey, SportsbookRankingRow[]>>
  >((acc, key) => {
    acc[key] = [...rows].sort((a, b) => b[key].value - a[key].value);
    return acc;
  }, {});
};

/** Maps the top-sports / top-leagues envelope into per-metric row lists. */
export const parseSportsbookTopSportsResponse = (
  body: unknown,
): SportsbookTopSportsData => {
  const envelope = body as { data?: unknown } | null | undefined;
  const payload = envelope?.data ?? body;

  if (!payload || typeof payload !== "object") {
    return { byMetric: {} };
  }

  const record = payload as Record<string, unknown>;
  const rankings = record.rankings;

  if (rankings && typeof rankings === "object" && !Array.isArray(rankings)) {
    const byMetric: SportsbookTopSportsData["byMetric"] = {};

    for (const [key, value] of Object.entries(
      rankings as Record<string, unknown>,
    )) {
      if (RANKINGS_META_KEYS.has(key) || !Array.isArray(value)) continue;
      byMetric[key as SportsbookTopSportsMetricKey] =
        normalizeSportsbookRankingRows(value);
    }

    return { byMetric };
  }

  return {
    byMetric: buildByMetricFromRows(normalizeSportsbookRankingRows(payload)),
  };
};

export const parseSportsbookTopLeaguesResponse = parseSportsbookTopSportsResponse;

export const getSportsbookMetricValue = (
  row: SportsbookRankingRow,
  metric: SportsbookTopSportsMetricKey,
): number => row[metric]?.value ?? 0;

export const getSportsbookMetricPercentChange = (
  row: SportsbookRankingRow,
  metric: SportsbookTopSportsMetricKey,
): number | null => row[metric]?.percent_change ?? null;
